import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams, type Location } from 'react-router-dom'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { authErrorMessage } from '@/lib/auth-errors'
import { sendPasswordResetEmail } from '@/lib/callables'
import { cn } from '@/lib/utils'
import biddleLogo from '@/assets/biddle-logo.png'

type Mode = 'signin' | 'signup' | 'forgot'

export function SignInModal() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const next = useMemo(() => searchParams.get('next') || '/search', [searchParams])
  const background = (location.state as { background?: Location } | null)?.background

  const {
    user,
    isGuest,
    isLoading,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signUpWithEmail,
    continueAsGuest,
    pendingLink,
  } = useAuth()

  const [mode, setMode] = useState<Mode>('signin')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKey)
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setEntered(true))
    })
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
      window.cancelAnimationFrame(id)
    }
    // dismiss is stable enough for this mount lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Only complete the redirect once a real (non-guest) session exists.
    if (!isLoading && user && !isGuest) {
      navigate(next, { replace: true })
    }
  }, [isLoading, user, isGuest, navigate, next])

  /**
   * Closing without signing in must not return to a RequireAuth route (redirect loop).
   * Prefer restoring the background location (keeps campaign letter state mounted).
   * Campaign letter pages (`/o…`) are public — return there when that was the next target.
   */
  function dismiss() {
    setEntered(false)
    window.setTimeout(() => {
      if (background) {
        navigate(background, { replace: true })
        return
      }
      navigate(next.startsWith('/o') ? next : '/search', { replace: true })
    }, 200)
  }

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      await action()
    } catch (e) {
      setError(authErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-navy/70 p-4 backdrop-blur-sm transition-opacity duration-200 ease-out',
        entered ? 'opacity-100' : 'opacity-0',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-desc"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dismiss()
      }}
    >
      <Card
        className={cn(
          'relative max-h-[min(90dvh,720px)] w-full max-w-md overflow-y-auto border-0 shadow-2xl transition-all duration-200 ease-out',
          entered ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-1 scale-[0.98] opacity-0',
        )}
      >
        {/* Out of flow so it never shifts header/logo padding */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-end p-3">
          <button
            type="button"
            onClick={dismiss}
            className="pointer-events-auto rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
            aria-label="Close sign in"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <CardHeader className="items-center px-5 pb-2 pt-8 text-center">
          <img src={biddleLogo} alt="Biddle" className="my-3 h-10 w-auto" />
          <CardDescription id="signin-desc" className="mt-1 px-4">
            Express interest in any Australian home — directly to the owner
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingLink ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              An account already exists for <strong>{pendingLink.email}</strong>. Sign in with your
              existing method (Apple, Google, or email). We&apos;ll link the new provider
              automatically.
            </div>
          ) : null}

          {isGuest && !user ? (
            <p className="rounded-xl bg-surface px-3 py-2 text-center text-xs text-muted">
              You&apos;re browsing as a guest. Sign in to save your account.
            </p>
          ) : null}

          <div className="grid gap-2">
            <Button
              variant="outline"
              className="w-full"
              disabled={busy}
              onClick={() => run(signInWithApple)}
            >
              <AppleIcon className="h-4 w-4" />
              Continue with Apple
            </Button>
            <Button
              variant="outline"
              className="w-full"
              disabled={busy}
              onClick={() => run(signInWithGoogle)}
            >
              <GoogleIcon className="h-4 w-4" />
              Continue with Google
            </Button>
          </div>

          <div className="relative py-1 text-center text-xs text-muted">
            <span className="bg-white px-2">or email</span>
            <div className="absolute inset-x-0 top-1/2 -z-10 border-t border-border" />
          </div>

          {mode === 'signup' ? (
            <div className="space-y-1">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          ) : null}

          <div className="space-y-1">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {mode !== 'forgot' ? (
            <div className="space-y-1">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          ) : null}

          {mode === 'signup' ? (
            <label className="flex items-start gap-2 text-sm text-muted">
              <input
                type="checkbox"
                className="mt-1"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              <span>
                I agree to the{' '}
                <a
                  className="text-biddle-blue underline"
                  href="https://biddle.io/terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms
                </a>{' '}
                and{' '}
                <a
                  className="text-biddle-blue underline"
                  href="https://biddle.io/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          ) : null}

          {error ? <p className="text-sm text-danger">{error}</p> : null}
          {info ? <p className="text-sm text-success">{info}</p> : null}

          <Button
            className="w-full"
            variant="primary"
            disabled={busy || (mode === 'signup' && !accepted)}
            onClick={() =>
              run(async () => {
                if (mode === 'signin') await signInWithEmail(email, password)
                else if (mode === 'signup') await signUpWithEmail(name, email, password)
                else {
                  await sendPasswordResetEmail(email)
                  setInfo('If an account exists, a reset email has been sent.')
                }
              })
            }
          >
            {busy ? <Spinner className="border-white border-t-transparent" /> : null}
            {mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send reset email'}
          </Button>

          <div className="flex flex-wrap justify-between gap-2 text-sm">
            {mode === 'signin' ? (
              <>
                <button type="button" className="text-biddle-blue" onClick={() => setMode('signup')}>
                  Create account
                </button>
                <button type="button" className="text-muted" onClick={() => setMode('forgot')}>
                  Forgot password?
                </button>
              </>
            ) : (
              <button type="button" className="text-biddle-blue" onClick={() => setMode('signin')}>
                Back to sign in
              </button>
            )}
          </div>

          {!user ? (
            <Button
              variant="ghost"
              className="w-full"
              disabled={busy}
              onClick={() => {
                continueAsGuest()
                // Guests can't open Biddles/Properties/Chat — land on search,
                // unless they came from a public campaign letter page.
                if (background?.pathname.startsWith('/o')) {
                  navigate(background, { replace: true })
                  return
                }
                navigate(next.startsWith('/o') ? next : '/search', { replace: true })
              }}
            >
              Continue without signing in
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden fill="currentColor">
      <path d="M16.365 1.43c0 1.14-.415 2.202-1.207 3.033-.87.92-2.098 1.623-3.246 1.528-.147-1.09.42-2.245 1.196-3.046C13.99 1.905 15.25 1.18 16.365 1.43zM20.82 17.33c-.59 1.36-.87 1.96-1.63 3.16-1.06 1.66-2.55 3.73-4.4 3.75-1.64.02-2.07-1.07-4.3-1.06-2.24.01-2.71 1.08-4.35 1.06-1.85-.02-3.27-1.88-4.33-3.54C-.1 17.7-1.2 12.3.95 8.95c1.21-1.9 3.13-3.01 4.94-3.01 1.84 0 3 .99 4.52.99 1.47 0 2.37-1 4.52-1 1.61 0 3.31.88 4.52 2.4-3.97 2.18-3.33 7.86 1.37 9.97z" />
    </svg>
  )
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}
